/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'

import PropTypes from 'prop-types'

import {Flex, Stack, Text} from '@chakra-ui/react'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import PromoPopover from '@salesforce/retail-react-app/app/components/promo-popover'
import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'

import {FormattedNumberWithSpace} from '@epmc-mach/accelarator/overrides/app/utils/currency-utils'

import {useItemVariant} from '/overrides/app/components/item-variant'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, currency, productAttributesStyles, ...props}) => {
    const variant = useItemVariant() ?? {}
    const {data: basket} = useCurrentBasket()
    const promotionIds = variant?.priceAdjustments?.map((adj) => adj.promotionId) ?? []

    // Fetch all the promotions given by price adjustments. We display this info in
    // the promotion info popover when applicable.
    const {data: res} = usePromotions(
        {
            parameters: {
                ids: promotionIds.join(',')
            }
        },
        {
            enabled: promotionIds.length > 0
        }
    )
    const promos = res?.data || []
    // Create a mapping of variation values to their associated attributes. This allows us
    // the render the readable names/labels rather than variation value IDs.
    const variationValues = Object.keys(variant.variationValues || []).map((key) => {
        const value = variant.variationValues[key]
        const attr = variant.variationAttributes?.find((attr) => attr.id === key)
        return {
            id: key,
            name: attr?.name || key,
            value: attr.values.find((val) => val.value === value)?.name || value
        }
    })

    const {tCart} = useTranslationHelpers()

    const sizeAndQuantityMessage = `UK ${variant.c_sizeUK} | EU ${variant.c_size} | x ${variant.quantity}`
    const commercialColor = variant?.c_colourCommercial
        ? `${tCart?.summaryColour} ${variant?.c_colourCommercial}`
        : ''

    return (
        <Stack spacing={1} flex={1} gap="4px" mt="4px" {...props}>
            <Flex justifyContent="space-between" alignItems="center">
                {/* Color */}
                {variant?.c_colourCommercial && (
                    <Text lineHeight={1} color={productAttributesStyles?.color?.hex} fontSize="md">
                        <span>{commercialColor}</span>
                    </Text>
                )}

                {variant?.priceAdjustments?.[0] && (
                    <Text lineHeight={1} color="error" fontSize="lg" fontWeight="normal">
                        <FormattedNumberWithSpace
                            currency={variant.currency || currency || basket?.currency}
                            value={
                                variant.priceAdjustments[0].appliedDiscount?.type === 'bonus'
                                    ? 0
                                    : variant.price + variant.priceAdjustments[0].price
                            }
                        />
                    </Text>
                )}
            </Flex>

            {/* Size, Quantity and Price */}
            <Flex justifyContent="space-between" alignItems="center">
                <Text
                    lineHeight={1}
                    color={productAttributesStyles?.color?.hex}
                    fontSize={productAttributesStyles?.size}
                >
                    <span>{sizeAndQuantityMessage}</span>
                </Text>

                <Text
                    lineHeight={1}
                    color={productAttributesStyles?.color?.hex}
                    fontSize="lg"
                    textDecoration={variant?.priceAdjustments?.[0] ? 'line-through' : 'none'}
                >
                    <FormattedNumberWithSpace
                        currency={variant.currency || currency || basket?.currency}
                        value={variant.price}
                    />
                </Text>
            </Flex>

            {/* {variant?.priceAdjustments?.length > 0 && (
                <Flex alignItems="center">
                    <Text
                        lineHeight={1}
                        color={productAttributesStyles?.color?.hex}
                        fontSize={productAttributesStyles?.size}
                    >
                        <FormattedMessage
                            defaultMessage="Promotions"
                            id="item_attributes.label.promotions"
                        />
                        {': '}
                        <Text as="span" color="green.500">
                            <FormattedNumberWithSpace
                                currency={currency || basket?.currency}
                                value={variant?.priceAdjustments[0].price}
                            />
                        </Text>
                    </Text>
                    <PromoPopover ml={2}>
                        <Stack>
                            {promos?.map((promo) => (
                                <Text key={promo?.id} fontSize="sm">
                                    calloutMsg = {promo?.calloutMsg}
                                </Text>
                            ))}
                        </Stack>
                    </PromoPopover>
                </Flex>
            )} */}
        </Stack>
    )
}

ItemAttributes.displayName = 'ItemAttributes'

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    currency: PropTypes.string,
    productAttributesStyles: PropTypes.object
}

export default ItemAttributes
